export const ServiceEndpoints = {
  GOOGLE_MAPS_API_BASE: "https://maps.googleapis.com",
  OPENAI_API_BASE: "https://api.openai.com/v1/responses",
  DICEBEAR_API_BASE: "https://api.dicebear.com/7.x",
  PLACEHOLDER_IMAGE_BASE: "https://placehold.co",

  // Business endpoints
  BUSINESS_MINE: "/api/v1/business/mine",
  BUSINESS_BY_ID: "/api/v1/business",
  BUSINESS_BY_USER: "/api/v1/business/user",

  // Business config — per-business feature toggles & settings. Each field
  // (brandColor, slotCalendar, enableOnlineOrdering, ...) is its own jsonb
  // column on the `business_config` table, fully independent of the
  // business DTO/moreDetails. No dedicated _BASE constant: suffix off
  // BUSINESS_BY_ID the same way markBusinessOnboarded does.
  //   GET    {BUSINESS_BY_ID}/{businessId}/config   fetch all fields, raw
  //                                                   per-field shape (see
  //                                                   BusinessConfigData)
  //   PUT    {BUSINESS_BY_ID}/{businessId}/config   upsert one or more
  //                                                   fields — body is a
  //                                                   partial map, only keys
  //                                                   present are written;
  //                                                   each write fully
  //                                                   REPLACES that column
  //                                                   (no server merge)
  //   DELETE {BUSINESS_BY_ID}/{businessId}/config   disable all fields
  //                                                   (reset to db defaults)

  // Business collections (curated groups of businesses)
  //   GET    {BASE}?name={query}                        search by name
  //   GET    {BASE}?creatorId={userId}                  a lead's own collections
  //   GET    {BASE}/{id}?includeBusinessDetails={bool}   one collection + members
  //   POST   {BASE}                                      create
  //   PUT    {BASE}/{id}                                 update (full replace)
  //   DELETE {BASE}/{id}                                 delete
  BUSINESS_COLLECTIONS_BASE: "/api/v1/business-collections",

  // Listing collections (curated groups of product listings) — symmetrical to
  // business collections above, just with `collection` holding products.
  //   GET {BASE}?name={query}&creatorId={userId}          search, both optional
  //   GET {BASE}/{id}?includeListingDetails={bool}         one collection + members
  LISTING_COLLECTIONS_BASE: "/api/v1/listing-collections",

  // Business sections — a per-business tree used to group products for
  // browsing (unlimited depth, e.g. "Mixer Grinder" > "500W"). A product
  // references one node via `details.sectionId`.
  //   GET    {BASE}/business/{businessId}                  public: full nested tree
  //   POST   {BASE}                                         create (JWT)
  //   PUT    {BASE}/{sectionId}                             rename/move, full replace (JWT)
  //   DELETE {BASE}/{sectionId}?cascade={bool}               delete (JWT)
  //   PUT    {BASE}/reorder                                  bulk sibling reorder (JWT)
  // IMPORTANT: every write endpoint above ALWAYS returns HTTP 200, even on
  // failure — check `processingStatus.status` ("SUCCESS"/"FAILED"), never
  // `response.ok`. See BusinessSectionApiService.
  BUSINESS_SECTIONS_BASE: "/api/v1/business-sections",

  // Product <-> business-section assignment (standard products/* envelope,
  // NOT the always-200 shape above). PUT `${PRODUCT_SECTION_BASE}/{productId}/section`
  // with `{ sectionId: string | null }` (null clears). See ProductApiService.updateProductSection.
  PRODUCT_SECTION_BASE: "/api/v1/products",
  // PUT `${UPDATE_PRODUCT}/{productId}` — full editor save and voice patches.
  UPDATE_PRODUCT: "/api/v1/products/update-product",

  // Business Orders endpoints
  ORDER_CREATE: "/api/v1/orders/create-order",
  ORDER_GET_BY_BUSINESS: "/api/v1/orders/get-orders-by-business-id",
  ORDER_RESPOND: "/api/v1/orders/respond-to-order",

  // Subscriptions endpoints (business auto-pay)
  SUBSCRIPTION_USER: "/api/v2/subscriptions/user",
  SUBSCRIPTION_BUSINESS_BASE: "/api/v2/subscriptions/business",
  SUBSCRIPTION_CANCEL: "/api/v2/subscriptions/cancel",
  SUBSCRIPTION_PLANS: "/api/v2/subscriptions/plans",

  // Onboarding Program endpoints (captain/lead facing)
  ONBOARDING_CREATE_PENDING: "/api/v1/onboarding/create-pending-business",
  ONBOARDING_GET_PENDING_BY_CAPTAIN:
    "/api/v1/onboarding/get-pending-businesses-by-captain-id",
  /** Retries payment-link generation for an existing UNPAID row by pendingId.
   *  Does NOT resubmit the draft — see buildRetryPaymentLinkPayload. Replaces
   *  the earlier approach of re-calling ONBOARDING_CREATE_PENDING, which broke
   *  against the server's 1:1 nine_setup<->pending_business guard (see
   *  OnboardingApiService.retryPaymentLink). */
  ONBOARDING_RETRY_PAYMENT_LINK: "/api/v1/onboarding/retry-payment-link",
  ONBOARDING_MARK_PAID_BASE: "/api/v1/onboarding", // + /{pendingId}/mark-paid
  CAPTAIN_SUMMARY: "/api/v1/captains/get-captain-summary",
  CAPTAIN_CREATE: "/api/v1/captains/create-captain",
  UPDATE_CAPTAIN: "/api/v1/captains", // + /{captainId}
  CAPTAINS_BY_LEAD: "/api/v1/captains/get-captains-by-lead-id",
  CAPTAIN_SETTLE: "/api/v1/captains/settle-captain",
  // Generic settlements (any payee role) — see the "Settlements — Backend
  // Handover" doc. Not under /captains/: settlements belong to users.
  SETTLEMENTS_LIST: "/api/v1/user/settlements/get-settlements",
  SETTLEMENT_REQUEST: "/api/v1/user/settlements/request-settlement",
  SETTLEMENT_REQUESTS: "/api/v1/user/settlements/get-settlement-requests",
  SETTLEMENT_REQUEST_CANCEL:
    "/api/v1/user/settlements/cancel-settlement-request",
  SETTLEMENT_RECORD: "/api/v1/user/settlements/record-settlement",
  // Admin -> Settlements (admin-general; GET list ?status=, POST settle).
  // Admin-only; admin-general forwards to the user service, which owns the data.
  ADMIN_SETTLEMENT_REQUESTS: "/api/v1/admin/settlements/requests",
  ADMIN_SETTLEMENT_SETTLE:
    "/api/v1/admin/settlements/requests/{requestId}/settle",

  // Admin -> Businesses -> Approvals. Tag `business-approval` in the User
  // Service OpenAPI spec; these two ARE the whole controller (verified
  // 2026-09-05). Distinct from ONBOARDING_GET_PENDING_BY_CAPTAIN above: same
  // `pending_businesses` table, but the admin projection - every captain's
  // rows, plus captainId / nineSetupId / rejectionReason / reviewedBy /
  // reviewedAt.
  BUSINESS_PENDING_LIST: "/api/v1/user/businesses/pending/list",
  /** Approve or reject one pending row. `decision` takes the STATUS vocabulary
   *  ("APPROVED" / "REJECTED"), NOT a verb form - the spec types it as a bare
   *  string with no enum, so this was confirmed against a live call rather than
   *  inferred. See BusinessDecision in interfaces/AdminApprovalObject.ts. */
  BUSINESS_DECISION: "/api/v1/user/businesses/decision",

  // Admin -> Businesses -> Subscriptions. A DIFFERENT service from the
  // /api/v2/subscriptions/* endpoints above (user-facing), and it answers with
  // a third envelope shape - `{errors, status:{code}, contents}` - which
  // ApiEnvelopeHelper tolerates explicitly. GET with a comma-separated,
  // lowercase status list, e.g. `?status=active,paused`.
  BUSINESS_SUBSCRIPTIONS_LIST: "/api/v1/admin/business-subscriptions/list",

  // Admin -> Businesses -> All Businesses. GET, bearer, same third envelope
  // shape as business-subscriptions above - but here `contents` is an
  // array-WRAPPED SINGLE page object (coerceSingle handles it), not a flat
  // array of rows: `{content:[...], page, size, totalElements, totalPages}`.
  // `?expired=true` for the expired sub-tab; OMITTING `expired` is the
  // non-expired default (confirmed against a live call - there's no
  // `expired=false` variant). `?page=&size=` for pagination.
  BUSINESS_LIST: "/api/v1/admin/businesses/list",

  // Admin -> Businesses -> All Businesses -> extend a business's subscription
  // end date by N days (a manual override, e.g. for goodwill or a payment
  // that landed late). POST, bearer, id in the path + `days` as a query
  // param: `{BASE}/{businessId}/extend-subscription?days=N`. Same third
  // envelope shape; `contents` is array-wrapped (coerceSingle), same as
  // BUSINESS_DECISION's response. Same `/admin/businesses` base as
  // BUSINESS_LIST above.
  BUSINESS_EXTEND_SUBSCRIPTION_BASE: "/api/v1/admin/businesses",

  // Nine Setup pipeline (captain sales → curator build, table `nine_setup`).
  // Confirmed against the User Service OpenAPI spec (2026-08-01):
  //   https://ninefinds.com/api/v1/user/v3/api-docs → nine-setup-controller
  //
  // Verb-in-path RPC like the onboarding endpoints above — NOT plain REST. In
  // particular update is a POST with the id in the BODY (no id in the path),
  // and the list call takes no query parameters at all.
  NINE_SETUP_CREATE: "/api/v1/nine-setup/create-nine-setup",
  NINE_SETUP_UPDATE: "/api/v1/nine-setup/update-nine-setup",
  NINE_SETUP_UPDATE_ENQUIRY: "/api/v1/nine-setup/update-nine-setup-enquiry",
  NINE_SETUP_LIST: "/api/v1/nine-setup/get-nine-setups",
  NINE_SETUP_GET: "/api/v1/nine-setup/get-nine-setup", // + /{id}
  NINE_SETUP_DELETE: "/api/v1/nine-setup/delete-nine-setup", // + /{id}
  NINE_SETUP_CALENDAR: "/api/v1/nine-setup/onboardings-by-day",
  // Rows filtered by status, e.g. `?status=approved` for the curation queue.
  // DEPLOYED and verified against https://ninefinds.com/api/v1/user/v3/api-docs
  // on 2026-08-02: `status` is a required query parameter. (An earlier comment
  // here said it wasn't deployed — that was written before the backend shipped
  // it and was never updated.)
  NINE_SETUP_BY_STATUS: "/api/v1/nine-setup/get-nine-setups-by-status",

  // Owner sign-off on a page built through the Nine Setup pipeline (flips
  // BusinessDTO.isOnboarded false → true) is `{BUSINESS_BY_ID}/mark-onboarded/
  // {businessId}` — built from BUSINESS_BY_ID in BusinessApiService rather
  // than given its own constant, since it's the same base path and two copies
  // of one literal can only ever drift. Migrated 2026-08-28 from the old
  // `confirm-onboarding` route (POST), which the user service never actually
  // implements — the real route is PUT `mark-onboarded/{businessId}` (verb
  // BEFORE the id here, unlike more-details and subscription-cache which put
  // {businessId} first — confirmed against the controller's @PutMapping).

  // Support Agent (stateless chat — NineBusinessRs envelope, see SupportApiService)
  SUPPORT_ASK: "/api/v1/support/ask",

  // Dine In endpoints — Verticals API ("dine-in-controller"), confirmed
  // against its OpenAPI spec on 2026-08-11.
  //
  // These were RESHAPED wholesale from the original 2026-07-25 set: resources
  // pluralised, ids moved out of request bodies and into paths, and the
  // round update switched from POST to PUT. Nothing from the old shape
  // survives, so there is no back-compat path here — old constants were
  // removed rather than deprecated, since a stale one would only produce 404s.
  //
  //   POST   {DINE_IN_SESSIONS}                             create/join a table
  //   GET    {DINE_IN_SESSIONS}/{sessionId}                 one session snapshot
  //   PUT    {DINE_IN_SESSIONS}/{sessionId}                 close a session
  //   POST   {DINE_IN_SESSIONS}/{sessionId}/items           add items to the open round
  //   PUT    {DINE_IN_ROUNDS}/{roundId}                     close a round (the diner's "Order")
  //   PUT    {DINE_IN_ROUNDS}/{roundId}/decision            kitchen decides a whole round
  //   PUT    {DINE_IN_ITEMS}/{roundItemId}                  edit one item
  //   DELETE {DINE_IN_ITEMS}/{roundItemId}                  remove one item
  //   PUT    {DINE_IN_ITEMS}/{roundItemId}/decision         kitchen decides one item
  //   GET    {DINE_IN_BUSINESSES}/{businessId}/dine-in-sessions   the kitchen board
  //
  // The /decision, DELETE and item-edit routes are NOT called yet — listed so
  // the full surface is visible in one place when they get wired.
  DINE_IN_SESSIONS: "/api/v1/dining/dine-in-sessions",
  DINE_IN_ROUNDS: "/api/v1/dining/dine-in-rounds",
  DINE_IN_ITEMS: "/api/v1/dining/dine-in-items",
  /** businessId sits mid-path, which a single literal can't express — the
   *  full URL is composed in DineInApiService, same call made for
   *  BUSINESS_BY_ID + /confirm-onboarding above. */
  DINE_IN_BUSINESSES: "/api/v1/dining/businesses",

  // Representative image catalog (stock photos for sellers without their own)
  REPRESENTATIVE_IMAGE_SEARCH: "/api/v1/representative-images/search",
  REPRESENTATIVE_IMAGE_ADD: "/api/v1/manage/representative-images/add",

  // AI endpoints (AiApiService) — previously inlined per-call; centralized here
  // so a path only ever needs correcting in one place. AI_GENERATE_BULK_PRODUCTS
  // in particular is unverified against a spec (see AiApiService.generateBulkProducts'
  // javadoc) — the RESPONSE shape is confirmed, the path/request shape aren't.
  AI_FETCH_AUTOFILL: "/api/v1/products/fetchautofill",
  AI_FETCH_MARKET_PRICE: "/api/v1/products/fetchmarketprice",
  AI_GENERATE_BULK_PRODUCTS: "/api/v1/products/generateBulkProducts",
  /**
   * Photos → postable product drafts, with the originals stored to Supabase and
   * an image↔product map handed back. Contract in
   * interfaces/ImageAnalyzeObject.ts.
   *
   * The third of three easily-confused AI endpoints, so: use THIS one when the
   * seller has photos and you want products out. AI_FETCH_AUTOFILL reads only
   * the first photo and returns loose fields, creating and storing nothing.
   * AI_GENERATE_BULK_PRODUCTS takes seller text with no photos at all and
   * returns drafts in a different shape.
   *
   * multipart/form-data, and the `context` part MUST carry its own
   * application/json content type or the request is rejected with 415 before
   * any backend code runs — see AiConvertor.buildBulkImageAnalyzePayload.
   */
  AI_BULK_IMAGE_ANALYZE: "/api/v1/products/bulkImageAnalyze",

  /**
   * AI photo revamp (enhance / visualise-in-context / swap background) for a
   * single already-uploaded photo. Contract in interfaces/ImageRevampObject.ts.
   *
   * NOTE the different path prefix — /api/v1/imagegen/... rather than
   * /api/v1/products/... like every other AI endpoint above. Not a typo,
   * this genuinely lives on a separate controller.
   */
  AI_IMAGE_REVAMP: "/api/v1/imagegen/image-revamp",
  // GET `${AI_TURBO}/{businessId}` -> NineBusinessRs, contents[0] = { available }: whether Turbo can do anything
  // for this caller on this business (AiApiService.fetchTurboAvailability).
  AI_TURBO: "/api/v1/ai/turbo",

  /**
   * AI poster generation for a business — a QR-referral / service-showcase
   * poster (logo, business name, "OUR {servicesLabel}" heading, a QR code
   * with caption, product photo collage), print resolution. NOT a
   * promotional offer/announcement flyer — see PosterObject.ts's own doc
   * comment for the v2 contract this now sends. Same /api/v1/imagegen
   * controller as AI_IMAGE_REVAMP above, just a different action.
   */
  AI_IMAGE_POSTER: "/api/v1/imagegen/poster",

  /**
   * AI visiting-card generation for a business — logo + name/phone/address/
   * email composed into one 3.5x2in-proportioned card face, print
   * resolution, with a reserved QR corner the client composites into. Same
   * /api/v1/imagegen controller family as AI_IMAGE_POSTER above — see
   * VisitingCardObject.ts for the contract.
   */
  AI_IMAGE_VISITING_CARD: "/api/v1/imagegen/visiting-card",

  /**
   * AI NFC card face generation — a CR80 (bank-card, 85.6x54mm) face with 2mm
   * bleed: logo, themed ornament and the "TAP TO EXPLORE" contactless mark,
   * with one reserved QR square the client composites into. Same
   * /api/v1/imagegen family — see NfcCardObject.ts for the contract.
   */
  AI_IMAGE_NFC_CARD: "/api/v1/imagegen/nfc-card",

  /**
   * AI batch image edit — one revamp functionality over every product of a
   * listing collection, run through the provider's batch API. Contract in
   * interfaces/AiBatchObject.ts. Child paths (`/{batchId}`, `/{batchId}/cancel`,
   * `/{batchId}/revert`) are appended in AiBatchApiService.
   */
  AI_IMAGE_BATCHES: "/api/v1/imagegen/batches",
  // Quota is per-business: `${AI_IMAGE_BATCH_QUOTA_BASE}/{businessId}/batch-quota`,
  // built in AiBatchApiService (same convention as SLOT_CALENDAR_PRODUCT_BASE).
  AI_IMAGE_BATCH_QUOTA_BASE: "/api/v1/imagegen/businesses",

  /**
   * Catalogue PDF import. Sub-paths (`/config`, `/{id}`, `/{id}/start`,
   * `/{id}/drafts/{draftId}`, ...) are built in CatalogImportApiService.
   * Contract in interfaces/CatalogImportObject.ts.
   */
  CATALOG_IMPORTS: "/api/v1/catalog-imports",

  /**
   * Bulk image analysis v2 ("Smart photos"). Sub-paths (`/config`, `/sessions`,
   * `/sessions/{id}/images:batch`, `/plan/revise`, `/create-products`, ...) are built
   * in BulkImageAnalysisV2ApiService. Contract in interfaces/BulkImageAnalysisV2Object.ts.
   */
  BULK_IMAGE_ANALYSIS_V2: "/api/v2/bulk-image-analysis",

  // Slot Availability Calendar (verticals service) — Phase 1 of the backend's
  // "hybrid storage" proposal. See docs/plans/slot-availability-calendar-plan.md.
  // Base is per-product: `${SLOT_CALENDAR_PRODUCT_BASE}/{productId}/slot-calendar`
  // — built in SlotCalendarApiService rather than given its own constant per
  // product id, same convention BUSINESS_EXTEND_SUBSCRIPTION_BASE uses above.
  // Every child path (schedule-versions, availability, slots, blocks,
  // booking-entries, changes) is appended onto that base in the API service —
  // see the full endpoint table in the plan doc, section 3.
  SLOT_CALENDAR_PRODUCT_BASE: "/api/v1/verticals/products",
  // The one calendar endpoint that takes no productId — a syntax/date-
  // behaviour preview usable before the product (and therefore the real
  // calendar) exists.
  SLOT_CALENDAR_PREVIEWS: "/api/v1/verticals/slot-calendar-previews",

  // Voice capability (product service) — contract in interfaces/VoiceObject.ts
  // and product/docs/voice-api-contract.md (NineBackend repo).
  // `{skillId}` / `{requestId}` are substituted in VoiceApiService.
  VOICE_CAPABILITIES: "/api/v1/voice/capabilities",
  VOICE_ANALYZE: "/api/v1/voice/skills/{skillId}/analyze",
  VOICE_CREATE_PUBLISH: "/api/v1/voice/skills/product.create/publish",
  VOICE_CREATE_REQUESTS: "/api/v1/voice/skills/product.create/requests",
  VOICE_OUTCOME: "/api/v1/voice/requests/{requestId}/outcome",
};
